import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { FaTag } from '@react-icons/all-files/fa/FaTag';
import { FaSearch } from '@react-icons/all-files/fa/FaSearch';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import { FaInfoCircle } from '@react-icons/all-files/fa/FaInfoCircle';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import PageHeader from '../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../commons/PageAlert';
import ProfaseModal from '../../commons/ProfaseModal';
import { playSound } from '../../shared/audio/audio';
import {
  EmpresaEtiquetaConfigItem,
  EmpresaEtiquetaPane,
  EtiquetaChip,
  addEtiquetaChip,
  applyEmpresaEtiquetaMoveMany,
  canMoveEmpresaEtiqueta,
  etiquetaIdsToAssign,
  etiquetaSearchFromIds,
  filterEmpresasByQuery,
  filterEtiquetaChips,
  parseEtiquetaIdsFromSearch,
  partitionEmpresasByEtiquetas,
  removeEtiquetaChip,
} from './empresa-etiqueta-config';

const EMPRESA_ETIQUETA_DRAG = 'application/x-profase-empresa-etiqueta';
const EMPRESA_ETIQUETA_MOVE_SOUND = '/assets/audio/play-card.mp3';
const EMPRESA_ETIQUETA_MOVE_ALL_SOUND = '/assets/audio/shuffle-card.mp3';

export default function EmpresaEtiquetaConfigPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const selectedIds = useMemo(() => parseEtiquetaIdsFromSearch(searchParams), [searchParams]);
  const [etiquetas, setEtiquetas] = useState<EtiquetaChip[]>([]);
  const [empresas, setEmpresas] = useState<EmpresaEtiquetaConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState<EmpresaEtiquetaPane | null>(null);
  const dragRef = useRef<{ empresaId: number; from: EmpresaEtiquetaPane } | null>(null);
  const { alert, showToast } = usePageAlert();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [etiquetasRes, empresasRes] = await Promise.all([
          axios.get<EtiquetaChip[]>('api/etiquetas', {
            params: { page: 0, size: 1000, sort: 'nombre,asc' },
          }),
          axios.get<EmpresaEtiquetaConfigItem[]>('api/empresas/etiqueta-config'),
        ]);
        if (cancelled) {
          return;
        }
        setEtiquetas(etiquetasRes.data ?? []);
        setEmpresas(empresasRes.data ?? []);
      } catch {
        if (!cancelled) {
          showToast('error', 'No se pudieron cargar las etiquetas de empresas.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [showToast]);

  const suggestions = useMemo(
    () => filterEtiquetaChips(etiquetas, query, selectedIds),
    [etiquetas, query, selectedIds],
  );
  const selectedChips = useMemo(
    () => selectedIds
      .map(id => etiquetas.find(item => item.id === id))
      .filter((item): item is EtiquetaChip => Boolean(item)),
    [etiquetas, selectedIds],
  );
  const { conEtiqueta, sinEtiqueta } = useMemo(
    () => partitionEmpresasByEtiquetas(empresas, selectedIds),
    [empresas, selectedIds],
  );
  const [filterSin, setFilterSin] = useState('');
  const [filterCon, setFilterCon] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | null>(null);
  const sinVisibles = useMemo(() => filterEmpresasByQuery(sinEtiqueta, filterSin), [sinEtiqueta, filterSin]);
  const conVisibles = useMemo(() => filterEmpresasByQuery(conEtiqueta, filterCon), [conEtiqueta, filterCon]);
  const selectedEmpresa = useMemo(
    () => empresas.find(item => item.id === selectedEmpresaId) ?? null,
    [empresas, selectedEmpresaId],
  );
  const selectedEnSin = selectedEmpresa != null && sinEtiqueta.some(item => item.id === selectedEmpresa.id);
  const selectedEnCon = selectedEmpresa != null && conEtiqueta.some(item => item.id === selectedEmpresa.id);

  const setSelectedIds = useCallback((next: number[] | ((prev: number[]) => number[])) => {
    const ids = typeof next === 'function' ? next(selectedIds) : next;
    const params = etiquetaSearchFromIds(ids);
    const current = searchParams.toString();
    if (params.toString() === current) {
      return;
    }
    setSearchParams(params, { replace: true });
  }, [searchParams, selectedIds, setSearchParams]);

  const handleSelect = (chip: EtiquetaChip) => {
    setSelectedIds(prev => addEtiquetaChip(prev, chip.id));
    setQuery('');
  };

  const toggleEtiqueta = (etiquetaId: number, checked: boolean) => {
    setSelectedIds(prev => (
      checked ? addEtiquetaChip(prev, etiquetaId) : removeEtiquetaChip(prev, etiquetaId)
    ));
  };

  const selectableEtiquetaIds = useMemo(
    () => etiquetas.filter(item => item.habilitado !== false).map(item => item.id),
    [etiquetas],
  );

  const parseDragPayload = (e: React.DragEvent) => {
    const fromRef = dragRef.current;
    if (fromRef) {
      return fromRef;
    }
    const raw = e.dataTransfer.getData(EMPRESA_ETIQUETA_DRAG) || e.dataTransfer.getData('text/plain');
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as { empresaId: number; from: EmpresaEtiquetaPane };
    } catch {
      return null;
    }
  };

  const handleDragStart = (e: React.DragEvent, empresaId: number, from: EmpresaEtiquetaPane) => {
    if (selectedIds.length === 0) {
      e.preventDefault();
      showToast('info', 'Selecciona una etiqueta para asignar.');
      return;
    }
    const payload = { empresaId, from };
    dragRef.current = payload;
    e.dataTransfer.setData(EMPRESA_ETIQUETA_DRAG, JSON.stringify(payload));
    e.dataTransfer.setData('text/plain', JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, pane: EmpresaEtiquetaPane) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = selectedIds.length ? 'move' : 'none';
    setDragOver(pane);
  };

  const handleDragLeave = (e: React.DragEvent, pane: EmpresaEtiquetaPane) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    setDragOver(prev => (prev === pane ? null : prev));
  };

  const persistMoves = async (items: EmpresaEtiquetaConfigItem[], to: EmpresaEtiquetaPane) => {
    if (busy) {
      return;
    }
    if (selectedIds.length === 0) {
      showToast('info', 'Selecciona una etiqueta para asignar.');
      return;
    }
    if (!items.length) {
      return;
    }
    const previous = empresas;
    const ids = items.map(item => item.id);
    playSound(items.length === 1 ? EMPRESA_ETIQUETA_MOVE_SOUND : EMPRESA_ETIQUETA_MOVE_ALL_SOUND);
    setEmpresas(applyEmpresaEtiquetaMoveMany(empresas, ids, selectedIds, to));
    setBusy(true);
    try {
      await Promise.all(
        items.map(item => {
          const current = empresas.find(empresa => empresa.id === item.id) ?? item;
          return Promise.all(
            etiquetaIdsToAssign(current, selectedIds, to).map(etiquetaId => (
              to === 'con'
                ? axios.post(`api/empresas/${item.id}/etiquetas/${etiquetaId}`)
                : axios.delete(`api/empresas/${item.id}/etiquetas/${etiquetaId}`)
            )),
          );
        }),
      );
    } catch {
      setEmpresas(previous);
      showToast('error', 'No se pudo actualizar la etiqueta de la empresa.');
    } finally {
      setBusy(false);
    }
  };

  const handleDrop = async (e: React.DragEvent, to: EmpresaEtiquetaPane) => {
    e.preventDefault();
    setDragOver(null);
    const payload = parseDragPayload(e);
    dragRef.current = null;
    if (!payload?.empresaId) {
      return;
    }
    if (!canMoveEmpresaEtiqueta(payload.from, to, selectedIds)) {
      if (selectedIds.length === 0) {
        showToast('info', 'Selecciona una etiqueta para asignar.');
      }
      return;
    }
    const empresa = empresas.find(item => item.id === payload.empresaId);
    if (!empresa) {
      return;
    }
    await persistMoves([empresa], to);
  };

  return (
    <div style={s.page}>
      <PageHeader
        icon={<FaTag />}
        iconColor="#ec4899"
        title="Etiquetas"
        subtitle="Arrastra empresas entre columnas para asignar o quitar las etiquetas"
      />

      {alert && <PageAlert alert={alert} />}

      <div style={s.searchRow}>
        <div style={s.searchWrap}>
          <FaSearch style={s.searchIcon} />
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && suggestions[0]) {
                e.preventDefault();
                handleSelect(suggestions[0]);
              }
            }}
            placeholder="Buscar y agregar etiquetas…"
            aria-label="Buscar etiquetas"
            style={s.search}
          />
          {query.trim() ? (
            <div style={s.suggest} role="listbox" aria-label="Resultados de etiquetas">
              {suggestions.length === 0 ? (
                <div style={s.suggestEmpty}>Sin coincidencias</div>
              ) : (
                suggestions.map(chip => (
                  <button
                    key={chip.id}
                    type="button"
                    role="option"
                    style={s.suggestItem}
                    onClick={() => handleSelect(chip)}
                  >
                    <span style={{ ...s.chipDot, background: chip.color || '#ec4899' }} />
                    {chip.nombre}
                  </button>
                ))
              )}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          style={s.eyeBtn}
          aria-label="Ver todas las etiquetas"
          title="Ver todas las etiquetas"
          onClick={() => setPickerOpen(true)}
        >
          <FaEye size={14} />
        </button>
      </div>

      <EtiquetaPickerModal
        open={pickerOpen}
        etiquetas={etiquetas}
        selectedIds={selectedIds}
        onToggle={toggleEtiqueta}
        onSelectAll={() => setSelectedIds(selectableEtiquetaIds)}
        onClearAll={() => setSelectedIds([])}
        onClose={() => setPickerOpen(false)}
      />

      {selectedChips.length > 0 ? (
        <div style={s.chips} aria-label="Etiquetas seleccionadas">
          {selectedChips.map(chip => (
            <span
              key={chip.id}
              style={{
                ...s.chip,
                background: chip.color || '#ec4899',
                borderColor: chip.color || '#ec4899',
              }}
            >
              <span style={{ ...s.chipDot, background: '#fff' }} />
              {chip.nombre}
              <button
                type="button"
                style={s.chipRemove}
                aria-label={`Quitar ${chip.nombre}`}
                onClick={() => setSelectedIds(prev => removeEtiquetaChip(prev, chip.id))}
              >
                <FaTimes />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div style={s.split}>
        <EmpresaPane
          title="Sin etiqueta"
          side="sin"
          items={sinVisibles}
          total={sinEtiqueta.length}
          filter={filterSin}
          onFilterChange={setFilterSin}
          empty={selectedIds.length ? 'Todas las empresas tienen esas etiquetas' : 'Busca una etiqueta para filtrar'}
          loading={loading}
          canDrag={selectedIds.length > 0}
          selectedEmpresaId={selectedEmpresaId}
          onSelectEmpresa={setSelectedEmpresaId}
          dragOver={dragOver === 'sin'}
          onDragStart={(e, id) => handleDragStart(e, id, 'sin')}
          onDragOver={e => handleDragOver(e, 'sin')}
          onDragLeave={e => handleDragLeave(e, 'sin')}
          onDrop={e => { void handleDrop(e, 'sin'); }}
        />
        <div style={s.transfer}>
          <button
            type="button"
            style={{
              ...s.transferBtn,
              ...((busy || selectedIds.length === 0 || !selectedEnSin || !selectedEmpresa) ? s.transferBtnDisabled : {}),
            }}
            disabled={busy || selectedIds.length === 0 || !selectedEnSin || !selectedEmpresa}
            onClick={() => { if (selectedEmpresa) void persistMoves([selectedEmpresa], 'con'); }}
          >
            Seleccionar
          </button>
          <button
            type="button"
            style={{
              ...s.transferBtn,
              ...((busy || selectedIds.length === 0 || !selectedEnCon || !selectedEmpresa) ? s.transferBtnDisabled : {}),
            }}
            disabled={busy || selectedIds.length === 0 || !selectedEnCon || !selectedEmpresa}
            onClick={() => { if (selectedEmpresa) void persistMoves([selectedEmpresa], 'sin'); }}
          >
            Deseleccionar
          </button>
          <button
            type="button"
            style={{
              ...s.transferBtn,
              ...((busy || selectedIds.length === 0 || sinVisibles.length === 0) ? s.transferBtnDisabled : {}),
            }}
            disabled={busy || selectedIds.length === 0 || sinVisibles.length === 0}
            onClick={() => { void persistMoves(sinVisibles, 'con'); }}
          >
            Seleccionar TODO
          </button>
          <button
            type="button"
            style={{
              ...s.transferBtn,
              ...((busy || selectedIds.length === 0 || conVisibles.length === 0) ? s.transferBtnDisabled : {}),
            }}
            disabled={busy || selectedIds.length === 0 || conVisibles.length === 0}
            onClick={() => { void persistMoves(conVisibles, 'sin'); }}
          >
            Deseleccionar TODO
          </button>
        </div>
        <EmpresaPane
          title="Con etiqueta"
          side="con"
          items={conVisibles}
          total={conEtiqueta.length}
          filter={filterCon}
          onFilterChange={setFilterCon}
          empty={selectedIds.length ? 'Ninguna empresa tiene esas etiquetas' : 'Busca una etiqueta para filtrar'}
          loading={loading}
          canDrag={selectedIds.length > 0}
          selectedEmpresaId={selectedEmpresaId}
          onSelectEmpresa={setSelectedEmpresaId}
          dragOver={dragOver === 'con'}
          onDragStart={(e, id) => handleDragStart(e, id, 'con')}
          onDragOver={e => handleDragOver(e, 'con')}
          onDragLeave={e => handleDragLeave(e, 'con')}
          onDrop={e => { void handleDrop(e, 'con'); }}
        />
      </div>
    </div>
  );
}

function EtiquetaPickerModal({
  open,
  etiquetas,
  selectedIds,
  onToggle,
  onSelectAll,
  onClearAll,
  onClose,
}: {
  open: boolean;
  etiquetas: EtiquetaChip[];
  selectedIds: number[];
  onToggle: (etiquetaId: number, checked: boolean) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allEnabledIds = useMemo(
    () => etiquetas.filter(item => item.habilitado !== false).map(item => item.id),
    [etiquetas],
  );
  const visibles = useMemo(() => {
    const q = query.trim().toLowerCase();
    return etiquetas
      .filter(item => item.habilitado !== false)
      .filter(item => !q || (item.nombre ?? '').toLowerCase().includes(q))
      .slice()
      .sort((a, b) => (a.nombre ?? '').localeCompare(b.nombre ?? '', 'es'));
  }, [etiquetas, query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  return (
    <ProfaseModal
      open={open}
      titleId="empresa-etiqueta-picker-title"
      size="compact"
      scroll
    >
      <div style={s.modalHeader}>
        <h2 id="empresa-etiqueta-picker-title" className="profase-modal-title m-0">
          <FaEye style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Etiquetas
        </h2>
        <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
          Cerrar
        </button>
      </div>
      <input
        type="search"
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Buscar etiqueta…"
        aria-label="Buscar en la lista de etiquetas"
        style={s.modalSearch}
      />
      <div style={s.modalActions}>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={onSelectAll}
          disabled={allEnabledIds.length === 0 || allEnabledIds.every(id => selected.has(id))}
        >
          Seleccionar Todo
        </button>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={onClearAll}
          disabled={selectedIds.length === 0}
        >
          Deseleccionar Todo
        </button>
      </div>
      <div style={s.modalList} role="list">
        {visibles.length === 0 ? (
          <div style={s.modalEmpty}>Sin etiquetas</div>
        ) : (
          visibles.map(chip => {
            const checked = selected.has(chip.id);
            return (
              <label key={chip.id} style={s.modalRow} role="listitem">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={e => onToggle(chip.id, e.target.checked)}
                  aria-label={chip.nombre}
                />
                <span style={{ ...s.chipDot, background: chip.color || '#ec4899' }} />
                <span style={s.modalName}>{chip.nombre}</span>
              </label>
            );
          })
        )}
      </div>
    </ProfaseModal>
  );
}

function EmpresaPane({
  title,
  side,
  items,
  total,
  filter,
  onFilterChange,
  empty,
  loading,
  canDrag,
  selectedEmpresaId,
  onSelectEmpresa,
  dragOver,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  title: string;
  side: EmpresaEtiquetaPane;
  items: EmpresaEtiquetaConfigItem[];
  total: number;
  filter: string;
  onFilterChange: (value: string) => void;
  empty: string;
  loading: boolean;
  canDrag: boolean;
  selectedEmpresaId: number | null;
  onSelectEmpresa: (empresaId: number) => void;
  dragOver: boolean;
  onDragStart: (e: React.DragEvent, empresaId: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  const filtered = filter.trim().length > 0;
  const countLabel = side === 'con'
    ? (items.length === 1 ? 'Empresa seleccionada' : 'Empresas seleccionadas')
    : (items.length === 1 ? 'Empresa para seleccionar' : 'Empresas para seleccionar');
  return (
    <section style={s.pane}>
      <div style={s.paneHead}>
        <span style={s.paneTitle}>{title}</span>
      </div>
      <div
        style={{ ...s.paneBox, ...(dragOver ? s.paneDragOver : {}) }}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <div style={s.paneFilter}>
          <input
            type="search"
            value={filter}
            onChange={e => onFilterChange(e.target.value)}
            placeholder="Buscar RFC, razón social…"
            aria-label={`Buscar en ${title}`}
            style={s.paneFilterInput}
          />
        </div>
        <div style={s.paneBody}>
          {loading ? (
            <div style={s.paneEmpty}>Cargando empresas…</div>
          ) : items.length === 0 ? (
            <div style={s.paneEmpty}>{filtered ? 'Sin coincidencias' : empty}</div>
          ) : (
            items.map(item => (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                aria-pressed={item.id === selectedEmpresaId}
                aria-label={item.razonSocial || item.rfc || `Empresa ${item.id}`}
                draggable={canDrag}
                onClick={() => onSelectEmpresa(item.id)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectEmpresa(item.id);
                  }
                }}
                onDragStart={e => onDragStart(e, item.id)}
                style={{
                  ...s.empresaRow,
                  ...(canDrag ? s.empresaRowDrag : {}),
                  ...(item.id === selectedEmpresaId ? s.empresaRowSelected : {}),
                }}
              >
                <div style={s.empresaText}>
                  <div style={s.empresaName}>{item.razonSocial || '—'}</div>
                  <div style={s.empresaRfc}>{item.rfc || '—'}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <div style={s.paneFooter} role="status">
        <FaInfoCircle size={12} style={s.paneFooterIcon} aria-hidden />
        <span>
          {items.length} {countLabel}
          {filtered ? ` (${total})` : ''}
        </span>
      </div>
    </section>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 12,
  },
  searchRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    maxWidth: 468,
  },
  searchWrap: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
  },
  eyeBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    flexShrink: 0,
    padding: 0,
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    background: 'var(--crm-surface)',
    color: 'var(--crm-text-primary)',
    cursor: 'pointer',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
  },
  modalActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  modalSearch: {
    width: '100%',
    marginBottom: 10,
    padding: '8px 10px',
    fontSize: 13,
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text-primary)',
    boxSizing: 'border-box',
    outline: 'none',
  },
  modalList: {
    maxHeight: 'min(420px, 55vh)',
    overflow: 'auto',
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
  },
  modalRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    borderBottom: '1px solid var(--crm-border)',
    fontSize: 13,
    color: 'var(--crm-text-primary)',
    cursor: 'pointer',
  },
  modalName: {
    minWidth: 0,
    fontWeight: 600,
  },
  modalEmpty: {
    padding: 16,
    textAlign: 'center',
    color: 'var(--crm-text-muted)',
    fontSize: 13,
    fontStyle: 'italic',
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: 10,
    color: 'var(--crm-text-muted)',
    fontSize: 13,
  },
  search: {
    width: '100%',
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    padding: '8px 10px 8px 34px',
    fontSize: 13,
    outline: 'none',
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text-primary)',
    boxSizing: 'border-box',
  },
  suggest: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '100%',
    marginTop: 4,
    background: 'var(--crm-surface)',
    border: '1px solid var(--crm-border)',
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
    zIndex: 5,
    maxHeight: 240,
    overflow: 'auto',
  },
  suggestEmpty: {
    color: 'var(--crm-text-muted)',
    fontSize: 13,
    padding: '10px 12px',
    fontStyle: 'italic',
  },
  suggestItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    border: 'none',
    background: 'transparent',
    textAlign: 'left',
    padding: '8px 10px',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--crm-text-primary)',
    cursor: 'pointer',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: '1px solid',
    borderRadius: 999,
    padding: '6px 10px 6px 12px',
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
  },
  chipDot: {
    width: 8,
    height: 8,
    borderRadius: 99,
    flexShrink: 0,
  },
  chipRemove: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    color: '#fff',
    padding: 0,
    cursor: 'pointer',
    fontSize: 11,
  },
  split: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    gap: 16,
    alignItems: 'stretch',
    minHeight: 420,
  },
  transfer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 8,
    paddingTop: 28,
  },
  transferBtn: {
    padding: '9px 14px',
    background: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  transferBtnDisabled: {
    background: '#e2e8f0',
    color: '#94a3b8',
    cursor: 'not-allowed',
    opacity: 0.85,
  },
  pane: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 0,
  },
  paneBox: {
    height: 280,
    minHeight: 280,
    flexShrink: 0,
    overflow: 'hidden',
    background: 'var(--crm-surface)',
    color: 'var(--crm-text-primary)',
    border: '1px solid var(--crm-border)',
    borderRadius: 10,
    padding: 6,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    transition: 'outline 0.12s, background 0.12s',
  },
  paneDragOver: {
    outline: '2px solid #22c55e',
    outlineOffset: -2,
    background: 'var(--crm-surface-hover)',
  },
  paneHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  paneFilter: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
    paddingBottom: 4,
  },
  paneFilterInput: {
    flex: 1,
    minWidth: 0,
    padding: '6px 8px',
    fontSize: 12,
    border: '1px solid var(--crm-border)',
    borderRadius: 6,
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text-primary)',
    boxSizing: 'border-box',
    outline: 'none',
  },
  paneTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--crm-text-primary)',
  },
  paneBody: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  paneEmpty: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    fontSize: 13,
    color: 'var(--crm-text-muted)',
    fontStyle: 'italic',
    textAlign: 'center',
  },
  paneFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 11,
    color: 'var(--crm-text-muted)',
    paddingTop: 2,
  },
  paneFooterIcon: {
    flexShrink: 0,
    color: 'var(--crm-text-muted)',
  },
  empresaRow: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '8px 10px',
    border: '1px solid transparent',
    borderRadius: 6,
    background: 'transparent',
    color: 'var(--crm-text-primary)',
    fontSize: 13,
    userSelect: 'none',
    boxSizing: 'border-box',
  },
  empresaRowDrag: {
    cursor: 'grab',
  },
  empresaRowSelected: {
    background: 'var(--crm-surface-hover)',
    border: '1px solid #10b981',
  },
  empresaText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
    width: '100%',
    textAlign: 'left',
  },
  empresaName: {
    fontSize: 13,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: 'var(--crm-text-primary)',
  },
  empresaRfc: {
    fontSize: 11,
    color: '#60a5fa',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};
